import { ChangeDetectorRef, Component, inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { ModalBase } from 'morgana';
import { DialogComponent } from '@syncfusion/ej2-angular-popups';
import { RapidReviewModalService } from '@shared/component/rapid-review-modal/rapid-review-modal.service';
import { RapidReviewHeaderResponse } from '@gandalf/model/rapid-review-header-response';
import { RapidReviewEncounterResponse } from '@gandalf/model/rapid-review-encounter-response';
import { timer } from 'rxjs';
import { DATE_FORMATS } from '@shared/constants/date-format.constants';
import { TabAnimationDefaults } from '@shared/constants/tab.constants';
import { SharedModule } from '@shared/shared.module';
import { RapidReviewHistoryComponent } from '@shared/component/rapid-review-modal/rapid-review-history/rapid-review-history.component';
import { RapidReviewExamRecapComponent } from '@shared/component/rapid-review-modal/rapid-review-exam-recap/rapid-review-exam-recap.component';
import {
	RapidReviewAssessmentAndPlanComponent
} from '@shared/component/rapid-review-modal/rapid-review-assessment-and-plan/rapid-review-assessment-and-plan.component';
import { OptionItemResponse, OptionItemService } from '@core/option-item/option-item.service';
import { formatDate } from '@angular/common';

export interface RapidReviewModalData {
	encounterId: number;
	isCurrentEncounter: boolean;
}

@Component({
	selector: 'pms-rapid-review-modal',
	imports: [SharedModule, RapidReviewHistoryComponent, RapidReviewExamRecapComponent, RapidReviewAssessmentAndPlanComponent],
	templateUrl: './rapid-review-modal.component.html',
})
export class RapidReviewModalComponent extends ModalBase<RapidReviewModalData> implements OnInit {
	private rapidReviewModalService = inject(RapidReviewModalService);
	private changeDetectorRef = inject(ChangeDetectorRef);

	@ViewChild('modal')
	modal: DialogComponent;

	headerResponse: RapidReviewHeaderResponse;
	encounterDropdownList: OptionItemResponse<RapidReviewEncounterResponse, number>[];
	currentEncounter: RapidReviewEncounterResponse;
	dateFormat = DATE_FORMATS.MM_DD_YYYY;
	tabAnimation = TabAnimationDefaults;
	showTabs = true;
	tabIndex = 0;
	displayEncounterNotStartedNotification: boolean;

	readonly locale = inject(LOCALE_ID);

	ngOnInit() {
		this.getHeaderData().subscribe(response => {
			this.headerResponse = response;
			this.encounterDropdownList = OptionItemService.toOptionItems(
				response.encounters,
				encounter => encounter.encounterId,
				encounter => this.formatEncounterName(encounter),
			);
			this.currentEncounter = this.headerResponse.encounters.find(
				encounter => encounter.encounterId === this.data.encounterId,
			);
		});

		this.displayEncounterNotStartedNotification = !this.data.isCurrentEncounter;
		if (this.displayEncounterNotStartedNotification) {
			//Hide the notification after 5 seconds
			timer(5000).subscribe(() => this.displayEncounterNotStartedNotification = false);
		}
	}

	formatEncounterName(encounter: RapidReviewEncounterResponse): string {
		return formatDate(encounter.encounterDate, this.dateFormat, this.locale) + ' - ' + encounter.templateName;
	}

	getHeaderData() {
		return this.rapidReviewModalService.getRapidReviewHeader(this.data.encounterId);
	}

	isLoading() {
		return this.rapidReviewModalService.isLoading();
	}

	closeModal() {
		this.dynamicModalRef.close(this.modal);
	}

	onEncounterChange(encounter: RapidReviewEncounterResponse) {
		this.showTabs = false;
		this.changeDetectorRef.detectChanges();
		this.currentEncounter = encounter;
		this.showTabs = true;
	}

	isFirstExam(encounterId: number): boolean {
		const lastIndex = this.encounterDropdownList.length - 1;
		return this.encounterDropdownList[lastIndex].encounterId === encounterId;
	}

	isLastExam(encounterId: number): boolean {
		return this.encounterDropdownList[0].encounterId === encounterId;
	}

	//Assuming these functions will be called only in valid scenarios, i.e. their corresponding buttons are enabled
	goToPreviousExam() {
		const currentIndex = this.encounterDropdownList.findIndex(
			encounter => encounter.encounterId === this.currentEncounter.encounterId,
		);
		this.currentEncounter = this.encounterDropdownList[currentIndex + 1];
	}

	goToNextExam() {
		const currentIndex = this.encounterDropdownList.findIndex(
			encounter => encounter.encounterId === this.currentEncounter.encounterId,
		);
		this.currentEncounter = this.encounterDropdownList[currentIndex - 1];
	}
}
